import { useEffect, useState } from "react";
import {
  getReferralEvents,
  subscribeReferralEvents,
  type ReferralEvent,
} from "@/lib/referralDebug";
import { getStoredReferralCode } from "@/lib/referral";

const FLAG_KEY = "grounded_referral_debug";

function shouldShow(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("refdebug") === "1") {
      localStorage.setItem(FLAG_KEY, "1");
      return true;
    }
    return localStorage.getItem(FLAG_KEY) === "1";
  } catch {
    return false;
  }
}

function urlRef(): string | null {
  try {
    return new URLSearchParams(window.location.search).get("ref");
  } catch {
    return null;
  }
}

export function ReferralDebugBanner() {
  const [visible, setVisible] = useState(false);
  const [events, setEvents] = useState<ReferralEvent[]>([]);
  const [stored, setStored] = useState<string | null>(null);
  const [ref, setRef] = useState<string | null>(null);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    setVisible(shouldShow());
  }, []);

  useEffect(() => {
    if (!visible) return;
    const refresh = () => {
      setEvents(getReferralEvents());
      setStored(getStoredReferralCode());
      setRef(urlRef());
    };
    refresh();
    const unsub = subscribeReferralEvents(refresh);
    const id = window.setInterval(refresh, 500);
    return () => {
      unsub();
      window.clearInterval(id);
    };
  }, [visible]);

  if (!visible) return null;

  const dismiss = () => {
    try { localStorage.removeItem(FLAG_KEY); } catch { /* ignore */ }
    setVisible(false);
  };

  return (
    <div
      style={{
        position: "fixed",
        bottom: 8,
        right: 8,
        zIndex: 2147483647,
        maxWidth: 380,
        maxHeight: "50vh",
        overflow: "auto",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        fontSize: 11,
        background: "rgba(20,12,8,0.95)",
        color: "#faf8f5",
        border: "1px solid #7b4f2e",
        borderRadius: 8,
        padding: 8,
        boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
      }}
    >
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
        <strong style={{ flex: 1 }}>[referral] debug</strong>
        <button
          onClick={() => setOpen((o) => !o)}
          style={{ background: "transparent", color: "#faf8f5", border: "1px solid #7b4f2e", borderRadius: 4, padding: "2px 6px", cursor: "pointer" }}
        >
          {open ? "−" : "+"}
        </button>
        <button
          onClick={dismiss}
          style={{ background: "transparent", color: "#faf8f5", border: "1px solid #7b4f2e", borderRadius: 4, padding: "2px 6px", cursor: "pointer" }}
        >
          ×
        </button>
      </div>
      <div>urlRef: <code>{JSON.stringify(ref)}</code></div>
      <div>stored: <code>{JSON.stringify(stored)}</code></div>
      {open && (
        <div style={{ marginTop: 6, borderTop: "1px dashed #7b4f2e", paddingTop: 6 }}>
          {events.length === 0 && <div style={{ opacity: 0.7 }}>no events yet</div>}
          {events.map((e, i) => (
            <div key={i} style={{ marginBottom: 4 }}>
              <div style={{ color: "#c9b49a" }}>
                {new Date(e.ts).toLocaleTimeString()} — {e.label}
              </div>
              <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                {JSON.stringify(e.data, null, 0)}
              </pre>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
